import { inject, Injectable, NgZone, signal } from '@angular/core';
import {
  catchError,
  concatMap,
  firstValueFrom,
  of,
  Subject,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { ClientBaseApi } from '../api/clientBase.api';
import { CsrfService } from './csrf.service';
import { LocalStorageService } from '../storage/local-storage.service';
import { ConfigService } from '../config/config.service';

export interface RegisterRequest {
  username: string;
  password: string;
}

export interface User {
  id: string;
  username: string;
}

export interface LoginRequest extends RegisterRequest {}

export interface RegisterResponse {
  user: {
    id: string;
    username: string;
  };
  csrfToken: string;
}

export interface ApiResponse {
  message: string;
  issue?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService extends ClientBaseApi {
  private loggedIn = false;
  private _isOffline = false;
  private apiUrl = '';
  private readonly loggedInStorageKey = 'isLoggedIn';
  private readonly localStorage = inject(LocalStorageService);
  private readonly csrfService = inject(CsrfService);
  private readonly myUser = signal<User | null>(null);
  private readonly zone = inject(NgZone);
  private readonly serviceApiUrl = inject(ConfigService).getConfig("apiUrl");

  public readonly getUser = this.myUser.asReadonly();

  private reconnectTries = 0;
  private connectionCheckStarted = false;

  public get isOffline(): boolean {
    return this._isOffline;
  }

  private readonly apiStatusChangeSubject = new Subject<boolean>();
  public readonly apiStatusChange$ = this.apiStatusChangeSubject.asObservable();
  public readonly isOnline = signal(false);

  constructor() {
    super();
  }

  public init() {
    this.startConnectionMonitor();
  }

  public checkForConnection(): void {
    if (!this.apiUrl) {
      return;
    }

    this.getData(this.serviceApiUrl ?? this.apiUrl + 'health')
      .pipe(
        tap(() => {
          if (this.isOffline) {
            this.apiStatusChangeSubject.next(true);
          }
          this.reconnectTries = 0;
          this.setIsOffline(false);
          this.isOnline.set(true);
        }),
        catchError(() => {
          if (!this.isOffline) {
            this.apiStatusChangeSubject.next(false);
          }
          this.reconnectTries++;
          this.setIsOffline(true);
          this.isOnline.set(false);
          return of(void 0);
        }),
      )
      .subscribe();
  }

  public isLoggedIn(): boolean {
    return this.loggedIn;
  }

  public setIsOffline(state: boolean): void {
    this._isOffline = state;
  }

  public setApiUrl(url: string) {
    this.apiUrl = url;
    this.startConnectionMonitor();
  }

  public initLoggedIn() {
    return firstValueFrom(
      this.getData(this.apiUrl + 'api/auth/me').pipe(
        tap((response: any) => {
          this.syncCsrfToken(response);
          this.loggedIn = response.hasOwnProperty('user');
          if (this.loggedIn) {
            this.persistLoggedInState();
            this.myUser.set(response.user);
          } else {
            this.clearLoggedInState();
            this.myUser.set(null);
          }
        }),
        catchError((error, _caught) => {
          this.loggedIn = false;
          this.myUser.set(null);
          console.warn(error);
          return of(void 0);
        }),
      ),
    );
  }

  public register(payload: RegisterRequest) {
    return this.postData<RegisterResponse>(
      this.apiUrl + 'api/auth/register',
      payload,
    ).pipe(tap((response) => this.syncCsrfToken(response)));
  }

  public login(payload: LoginRequest) {
    return this.postData<RegisterResponse | {message: string} >(
      this.apiUrl + 'api/auth/login',
      payload,
    ).pipe(tap((response) => this.syncCsrfToken(response)), concatMap(() => this.initLoggedIn()));
  }

  private startConnectionMonitor(): void {
    if (this.connectionCheckStarted || !this.apiUrl) {
      return;
    }

    this.connectionCheckStarted = true;

    this.zone.runOutsideAngular(() => {
      timer(150, 10000).subscribe(() => this.checkForConnection());
    });
  }

  private persistLoggedInState(): void {
    this.localStorage.setItem(this.loggedInStorageKey, 'true');
  }

  private clearLoggedInState(): void {
    this.localStorage.removeItem(this.loggedInStorageKey);
  }

  private syncCsrfToken(response: unknown): void {
    const csrfToken = this.extractCsrfToken(response);

    if (csrfToken) {
      this.csrfService.setCsrfToken(csrfToken);
    }
  }

  private extractCsrfToken(response: unknown): string | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const token = (response as Record<string, unknown>)['csrfToken'];
    return typeof token === 'string' ? token : null;
  }
}
